import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import { Loader2, Search, RotateCcw, Star, FileText, FileCheck2, MessageCircle, Trash2, Download, ExternalLink, Award, ChevronLeft, ChevronRight, X } from "lucide-react";
import { PROFESSIONS, COUNTRIES, STATES } from "@/data/mmp";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + "/api";
const ax = axios.create({ baseURL: API, timeout: 30000 });

const STATUSES = ["New", "Reviewed", "Shortlisted", "Contacted", "Archived"];
const STATUS_COLOR = {
  New: "bg-blue-100 text-blue-700",
  Reviewed: "bg-amber-100 text-amber-700",
  Shortlisted: "bg-emerald-100 text-emerald-700",
  Contacted: "bg-indigo-100 text-indigo-700",
  Archived: "bg-slate-200 text-slate-700",
};
const initials = (name) => (name || "?").split(" ").slice(0, 2).map((w) => w[0]?.toUpperCase() || "").join("") || "?";

export default function AdminApplications() {
  const [stats, setStats] = useState({});
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ q: "", profession: "", status: "", country: "", state: "" });
  const [selected, setSelected] = useState(null);

  const stateList = useMemo(() => STATES[filters.country] || [], [filters.country]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await ax.get("/admin-mmp/profiles", { params });
      setItems(data.items); setTotal(data.total);
      const s = await ax.get("/admin-mmp/stats"); setStats(s.data);
    } catch { toast.error("Failed to load applications"); } finally { setLoading(false); }
  }, [filters, page, pageSize]);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (p, status) => {
    try { await ax.patch(`/admin-mmp/profiles/${p.unique_id}`, { status }); toast.success("Status updated"); if (selected?.unique_id === p.unique_id) setSelected({ ...selected, status }); await load(); }
    catch { toast.error("Update failed"); }
  };
  const changeRating = async (p, rating) => {
    try { await ax.patch(`/admin-mmp/profiles/${p.unique_id}`, { rating }); if (selected?.unique_id === p.unique_id) setSelected({ ...selected, rating }); await load(); }
    catch { toast.error("Rating update failed"); }
  };
  const toggleRec = async (p) => {
    try { await ax.patch(`/admin-mmp/profiles/${p.unique_id}`, { recommended: !p.recommended }); toast.success("Updated"); if (selected?.unique_id === p.unique_id) setSelected({ ...selected, recommended: !p.recommended }); await load(); }
    catch { toast.error("Update failed"); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Delete ${p.unique_id} — ${p.full_name}?`)) return;
    try { await ax.delete(`/admin-mmp/profiles/${p.unique_id}`); toast.success("Deleted"); if (selected?.unique_id === p.unique_id) setSelected(null); await load(); }
    catch { toast.error("Delete failed"); }
  };

  const reset = () => { setFilters({ q: "", profession: "", status: "", country: "", state: "" }); setPage(1); };
  const fileUrl = (p) => p ? `${API}/files/${p}` : null;
  const pages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div data-testid="admin-applications">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-black text-[color:var(--mia-navy)] tracking-tight">Careers · Join Our Team Submissions</h1>
          <p className="text-[color:var(--mia-muted)] mt-2 text-sm">
            All CV & portfolio submissions from the &ldquo;Join Our Team&rdquo; portal. {total} total submission{total === 1 ? "" : "s"}.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <a href={`${API}/admin-mmp/export`} target="_blank" rel="noreferrer" className="mia-btn mia-btn-outline-navy text-xs" data-testid="apps-export"><Download className="w-4 h-4" /> Export CSV</a>
          <a href="/admin-marine-secure-2026" target="_blank" rel="noreferrer" className="mia-btn mia-btn-navy text-xs" data-testid="apps-open-full">Open Full MMP Portal <ExternalLink className="w-3.5 h-3.5" /></a>
        </div>
      </div>

      {/* STATS */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
        {[["Total", stats.total], ["New", stats.new], ["Reviewed", stats.reviewed], ["Shortlisted", stats.shortlisted], ["Contacted", stats.contacted], ["Recommended", stats.recommended]].map(([label, v]) => (
          <div key={label} className="rounded-xl border border-slate-100 bg-white px-4 py-3">
            <div className="text-[0.6rem] uppercase tracking-widest text-[color:var(--mia-muted)]">{label}</div>
            <div className="text-2xl font-black mt-1 text-[color:var(--mia-navy)]">{v ?? "—"}</div>
          </div>
        ))}
      </div>

      {/* FILTERS */}
      <div className="rounded-xl border border-slate-100 bg-white p-4 mb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="lg:col-span-2 flex items-center gap-2 border border-slate-200 rounded-lg px-3 py-2.5">
          <Search className="w-4 h-4 text-slate-400" />
          <input value={filters.q} onChange={(e) => { setFilters({ ...filters, q: e.target.value }); setPage(1); }} placeholder="Search name, email, unique ID, profession…" className="w-full bg-transparent focus:outline-none text-sm" data-testid="apps-search" />
        </div>
        <select value={filters.profession} onChange={(e) => { setFilters({ ...filters, profession: e.target.value }); setPage(1); }} className="border border-slate-200 rounded-lg px-3 py-2.5 text-sm bg-white" data-testid="apps-fil-profession"><option value="">All Professions</option>{PROFESSIONS.map((p) => <option key={p} value={p}>{p}</option>)}</select>
        <select value={filters.status} onChange={(e) => { setFilters({ ...filters, status: e.target.value }); setPage(1); }} className="border border-slate-200 rounded-lg px-3 py-2.5 text-sm bg-white" data-testid="apps-fil-status"><option value="">All Status</option>{STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}</select>
        <select value={filters.country} onChange={(e) => { setFilters({ ...filters, country: e.target.value, state: "" }); setPage(1); }} className="border border-slate-200 rounded-lg px-3 py-2.5 text-sm bg-white" data-testid="apps-fil-country"><option value="">All Countries</option>{COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}</select>
        <select value={filters.state} onChange={(e) => { setFilters({ ...filters, state: e.target.value }); setPage(1); }} disabled={stateList.length === 0} className="border border-slate-200 rounded-lg px-3 py-2.5 text-sm bg-white disabled:opacity-50" data-testid="apps-fil-state"><option value="">{stateList.length ? "All States" : "—"}</option>{stateList.map((s) => <option key={s} value={s}>{s}</option>)}</select>
        <button onClick={reset} className="text-xs text-[color:var(--mia-muted)] hover:text-[color:var(--mia-navy)] inline-flex items-center gap-1 justify-self-start" data-testid="apps-fil-reset"><RotateCcw className="w-3.5 h-3.5" /> Reset filters</button>
      </div>

      {/* LIST */}
      <div className="rounded-xl border border-slate-100 bg-white overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-[color:var(--mia-muted)]"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading submissions…</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-[color:var(--mia-muted)]">No submissions match your filters.</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {items.map((p) => (
              <li key={p.unique_id} className="p-4 sm:p-5 hover:bg-slate-50 flex items-center gap-4 flex-wrap sm:flex-nowrap" data-testid={`app-row-${p.unique_id}`}>
                <Avatar path={p.passport_photo_path} name={p.full_name} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-[color:var(--mia-navy)] truncate">{p.full_name}</span>
                    {p.recommended && <span className="inline-flex items-center gap-1 text-[0.6rem] font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full uppercase tracking-widest"><Award className="w-3 h-3" /> Recommended</span>}
                  </div>
                  <div className="font-mono text-[11px] text-slate-500 mt-0.5">{p.unique_id}</div>
                  <div className="text-sm text-[color:var(--mia-muted)] mt-1 truncate">{p.email} · {p.phone}</div>
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    <Pill className="bg-slate-100 text-slate-700">{p.profession}</Pill>
                    <Pill className="bg-slate-100 text-slate-700">{[p.state, p.country].filter(Boolean).join(", ")}</Pill>
                    <Pill className={STATUS_COLOR[p.status] || STATUS_COLOR.New}>{p.status}</Pill>
                    <Stars rating={p.rating || 0} onSet={(r) => changeRating(p, r)} />
                    <Doc ok={!!p.cv_path}>CV</Doc><Doc ok={!!p.passport_photo_path}>Photo</Doc><Doc ok={!!p.portfolio_path}>Portfolio</Doc>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap justify-end">
                  <select value={p.status} onChange={(e) => changeStatus(p, e.target.value)} className="border border-slate-200 rounded-md text-xs px-2 py-1.5 focus:outline-none" data-testid={`app-status-${p.unique_id}`}>
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <button onClick={() => toggleRec(p)} className={`p-2 rounded-md border border-slate-200 hover:border-amber-400 ${p.recommended ? "text-amber-500" : "text-slate-400"}`} title="Toggle Recommended" data-testid={`app-rec-${p.unique_id}`}><Award className="w-4 h-4" /></button>
                  {p.phone && <a href={`https://wa.me/${p.phone.replace(/\D/g, "")}?text=${encodeURIComponent(`Hello ${p.full_name}, this is Marine Infrastructure regarding your profile (${p.unique_id}).`)}`} target="_blank" rel="noreferrer" className="p-2 rounded-md border border-slate-200 hover:border-emerald-400 text-emerald-600" title="WhatsApp" data-testid={`app-wa-${p.unique_id}`}><MessageCircle className="w-4 h-4" /></a>}
                  {p.cv_path && <a href={fileUrl(p.cv_path)} target="_blank" rel="noreferrer" className="p-2 rounded-md border border-slate-200 hover:border-[color:var(--mia-navy)] text-[color:var(--mia-navy)]" title="Download CV" data-testid={`app-cv-${p.unique_id}`}><FileText className="w-4 h-4" /></a>}
                  {p.portfolio_path && <a href={fileUrl(p.portfolio_path)} target="_blank" rel="noreferrer" className="p-2 rounded-md border border-slate-200 hover:border-[color:var(--mia-navy)] text-[color:var(--mia-navy)]" title="Portfolio"><FileCheck2 className="w-4 h-4" /></a>}
                  <button onClick={() => setSelected(p)} className="text-xs font-semibold text-[color:var(--mia-navy)] hover:underline px-2" data-testid={`app-view-${p.unique_id}`}>View</button>
                  <button onClick={() => remove(p)} className="p-2 rounded-md border border-slate-200 hover:border-red-500 text-red-500" title="Delete" data-testid={`app-del-${p.unique_id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* PAGINATION */}
      <div className="flex items-center justify-between mt-4 text-sm text-[color:var(--mia-muted)]">
        <div>{total} total · page {page} of {pages}</div>
        <div className="flex gap-2">
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1.5 rounded-md border border-slate-200 disabled:opacity-40 inline-flex items-center gap-1"><ChevronLeft className="w-4 h-4" /> Prev</button>
          <button onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={page >= pages} className="px-3 py-1.5 rounded-md border border-slate-200 disabled:opacity-40 inline-flex items-center gap-1">Next <ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>

      {selected && <DetailDrawer p={selected} onClose={() => setSelected(null)} fileUrl={fileUrl} />}
    </div>
  );
}

function Pill({ className = "", children }) { return <span className={`text-[0.65rem] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full ${className}`}>{children}</span>; }

function Stars({ rating, onSet }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((r) => (
        <button key={r} onClick={() => onSet(r)} className={r <= rating ? "text-amber-500" : "text-slate-300 hover:text-amber-400"}><Star className="w-3.5 h-3.5" fill={r <= rating ? "currentColor" : "none"} /></button>
      ))}
    </div>
  );
}

function Doc({ ok, children }) {
  return <span className={`inline-flex items-center gap-1 text-[0.6rem] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded ${ok ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400"}`}>{ok ? "✓" : "—"} {children}</span>;
}

function Avatar({ path, name }) {
  const [err, setErr] = useState(false);
  const url = path ? `${API}/files/${path}` : null;
  if (!url || err) return <div className="w-11 h-11 rounded-full bg-[color:var(--mia-navy)]/10 text-[color:var(--mia-navy)] flex items-center justify-center font-bold text-sm shrink-0">{initials(name)}</div>;
  return <img src={url} onError={() => setErr(true)} alt={name} className="w-11 h-11 rounded-full object-cover bg-slate-100 shrink-0" />;
}

function DetailDrawer({ p, onClose, fileUrl }) {
  return (
    <div className="fixed inset-0 z-40 bg-black/50 flex justify-end" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-white border-l border-slate-100 overflow-y-auto" data-testid="app-detail-drawer">
        <div className="p-5 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white">
          <div>
            <div className="font-mono text-xs text-slate-500">{p.unique_id}</div>
            <div className="font-display font-black text-xl text-[color:var(--mia-navy)]">{p.full_name}</div>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-[color:var(--mia-navy)]"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3 text-sm">
          {[
            ["Email", p.email], ["Phone", p.phone], ["Gender", p.gender], ["DOB", p.date_of_birth],
            ["Age", p.age], ["Profession", p.profession + (p.other_profession ? ` — ${p.other_profession}` : "")],
            ["Experience", p.experience], ["Skills", p.skills],
            ["Country", p.country], ["State", p.state], ["City", p.city], ["Address", p.address],
            ["Summary", p.summary], ["Notes", p.admin_notes], ["Status", p.status],
            ["Rating", `${p.rating || 0}★`], ["Recommended", p.recommended ? "Yes" : "No"], ["Submitted", p.created_at],
          ].map(([k, v]) => v ? (
            <div key={k}><div className="text-[0.62rem] uppercase tracking-widest text-slate-500">{k}</div><div className="text-[color:var(--mia-navy)] whitespace-pre-wrap">{v}</div></div>
          ) : null)}
          <div className="pt-4 space-y-2">
            {p.cv_path && <a href={fileUrl(p.cv_path)} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-[color:var(--mia-navy)] hover:underline"><FileText className="w-4 h-4" /> Download CV <ExternalLink className="w-3 h-3" /></a>}
            {p.passport_photo_path && <a href={fileUrl(p.passport_photo_path)} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-[color:var(--mia-navy)] hover:underline"><FileCheck2 className="w-4 h-4" /> View Photo <ExternalLink className="w-3 h-3" /></a>}
            {p.portfolio_path && <a href={fileUrl(p.portfolio_path)} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-[color:var(--mia-navy)] hover:underline"><FileCheck2 className="w-4 h-4" /> Portfolio <ExternalLink className="w-3 h-3" /></a>}
          </div>
        </div>
      </div>
    </div>
  );
}
